import axios from 'axios';
import type { AxiosRequestConfig } from 'axios';
import Cookies from 'js-cookie';

import { CSRF_TOKEN_COOKIE_KEY } from 'reservations/base/constants/cookies';

export interface BentoResponseError {
  type: string;
  message: string;
}

export interface NextableResponseError {
  uri: string;
  message: string;
  code: number;
  data: Record<string, any>;
}

interface ResponseBody {
  data?: Record<string, any> | any[] | null;
  error?: BentoResponseError | NextableResponseError | null;
}

export class RequestError extends Error {
  code: number;
  data: Record<string, any>;

  constructor(message: string, code: number, data: Record<string, any>) {
    super(message);
    this.code = code;
    this.data = data;
  }
}

export const HTTP_REQUEST_METHODS = {
  GET: 'get',
  POST: 'post',
  PUT: 'put',
  PATCH: 'patch',
  DELETE: 'delete'
};

export function requestClient(
  endpoint: string,
  options: Record<string, any> = {}
) {
  const {
    nextableApiKey = '',
    headers: customHeaders = {},
    ...customConfig
  } = options;

  let config: AxiosRequestConfig = {
    headers: {
      'X-CSRFToken': Cookies.get(CSRF_TOKEN_COOKIE_KEY) ?? '',
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
      // NOTE: We're required to add a location's access_token on every request
      // to the Nextable API
      ...(!!nextableApiKey ? { 'x-zb-apikey': nextableApiKey } : {}),
      ...customHeaders
    },
    withCredentials: !nextableApiKey,
    ...customConfig
  };

  return axios(endpoint, config)
    .then(async response => {
      const { data: axiosData } = await response;
      const { data, error } = axiosData as ResponseBody;

      // NOTE: This check for `error != null` is intentional
      // because we're also guarding for when error is undefined, not just null.
      if (error != null) {
        // NOTE: This is the case where the Nextable API has an error
        // because all the Nextable requests return a 200 status code
        const { message, code, ...otherErrorData } =
          error as NextableResponseError;
        throw new RequestError(message, code, otherErrorData);
      }

      return data || axiosData;
    })
    .catch(error => {
      if (error.response) {
        // NOTE: Errors caught here should come from the Bentobox API
        let message, otherErrorData, statusCode;

        if (error.response.data?.error) {
          ({ message, ...otherErrorData } = error.response.data.error);
          statusCode = otherErrorData?.code ?? error.response.status;
        } else {
          statusCode = error.response.status;
          message = `${statusCode}: ${
            error.response.data?.detail ?? error.response.statusText
          }`;
          otherErrorData = { ...error };
        }

        throw new RequestError(message, statusCode, otherErrorData);
      } else if (error) {
        // NOTE: Errors caught here should come from Nextable API
        throw error;
      }
    });
}

export const restClient = {
  get: (endpoint: string, options: Record<string, any> = {}) =>
    requestClient(endpoint, {
      ...options,
      method: HTTP_REQUEST_METHODS.GET
    }),
  post: (endpoint: string, options: Record<string, any>) =>
    requestClient(endpoint, {
      ...options,
      method: HTTP_REQUEST_METHODS.POST
    }),
  put: (endpoint: string, options: Record<string, any>) =>
    requestClient(endpoint, {
      ...options,
      method: HTTP_REQUEST_METHODS.PUT
    }),
  patch: (endpoint: string, options: Record<string, any>) =>
    requestClient(endpoint, {
      ...options,
      method: HTTP_REQUEST_METHODS.PATCH
    }),
  delete: (endpoint: string, options: Record<string, any>) =>
    requestClient(endpoint, {
      ...options,
      method: HTTP_REQUEST_METHODS.DELETE
    })
};
